import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Globe2, Sprout, Users2 } from "lucide-react";

const HERO_TREE = "/hero-family-tree.jpg";

const ease = [0.22, 1, 0.36, 1];

const FEATURES = [
  { Icon: Globe2, label: "Real Stories, Real People" },
  { Icon: Sprout, label: "Stronger Communities" },
  { Icon: Users2, label: "A More Connected Tomorrow" },
];

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10 md:pt-14 pb-14 md:pb-20 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* LEFT — Copy */}
        <div className="lg:col-span-6 order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease }}
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="numeric-index tracking-[0.22em]">People · Places · Stories · Forever</span>
            </div>
            <div className="h-[3px] w-16 bg-[color:var(--rust)] rounded-full mb-6" />

            <h1
              data-testid="hero-headline"
              className="heritage-title leading-[1.06] tracking-[-0.01em] font-bold"
              style={{ fontSize: "clamp(36px, 4.6vw, 64px)", fontWeight: 700 }}
            >
              Every Family Has a Story Worth Preserving
            </h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.9 }}
              className="mt-6 max-w-lg text-[color:var(--ink-soft)] text-[16px] leading-[1.7]"
            >
              Build your family tree, honor loved ones, explore heritage, and
              connect generations across the world.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.7, ease }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link to="/app/register" data-testid="hero-cta-primary" className="btn-wood">
                Start Your Family Tree
                <span aria-hidden>→</span>
              </Link>
              <Link to="/category/heritage" data-testid="hero-cta-secondary" className="btn-ghost">
                Explore Global Heritage
                <span aria-hidden>→</span>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.75, duration: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4"
            >
              {FEATURES.map(({ Icon, label }) => (
                <div key={label} className="flex items-center gap-2.5 text-[13px] font-medium text-[color:var(--ink-soft)]" data-testid={`hero-feature-${label.split(",")[0].toLowerCase().replace(/\s+/g, "-")}`}>
                  <Icon size={16} className="text-[color:var(--sepia)]" />
                  {label}
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* RIGHT — Family tree illustration */}
        <div className="lg:col-span-6 order-1 lg:order-2 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease }}
            className="relative w-full aspect-square max-w-[560px] mx-auto"
          >
            <div
              aria-hidden
              className="hero-tree-glow absolute inset-4 rounded-full"
            />
            <img
              src={HERO_TREE}
              alt="A heritage tree holding circular family portraits from around the world"
              className="tree-grow-in hero-tree-img absolute inset-[6%] w-[88%] h-[88%] object-contain"
              loading="eager"
              data-testid="hero-tree-illustration"
            />

            {/* Side accent labels */}
            <div className="hidden md:block absolute top-2 -right-2 text-right">
              <div className="overline text-[color:var(--ink-soft)]">Different Roots</div>
              <div className="h-px w-24 bg-[color:var(--brass)]/60 mt-1 ml-auto" />
            </div>
            <div className="hidden md:block absolute top-14 -right-2 text-right">
              <div className="overline text-[color:var(--ink-soft)]">A Brighter Tomorrow</div>
              <div className="h-px w-28 bg-[color:var(--brass)]/60 mt-1 ml-auto" />
            </div>
            <div className="absolute bottom-0 right-2">
              <span className="font-italic-serif italic text-lg text-[color:var(--sepia)]">One Human Family</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Divider flourish */}
      <div className="max-w-3xl mx-auto px-6">
        <div className="flourish" />
      </div>
    </section>
  );
}
